import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AuthContext from '../../../../auth/AuthContext';
import AlertService from "../../../../services/AlertService";
import QuizMCQService from "../../../../services/QuizMCQService";
import QuizMCQExamService from "../../../../services/QuizMCQExamService";
import MCQQuestion from "./MCQQuestion";
import DownloadIcon from '../../../../assets/icons/download.svg';
import WhiteDownloadIcon from '../../../../assets/icons/WhiteDownloadIcon.svg';
import UploadIcon from '../../../../assets/icons/upload.svg';
import WhiteUploadIcon from '../../../../assets/icons/WhiteUploadIcon.svg';
import CalcIcon from '../../../../assets/icons/Calc.svg';
import WhiteCalcIcon from '../../../../assets/icons/WhiteCalcIcon.svg';
import QuizMCQImportTemplate from '../../../../assets/Quiz MCQ Import Template.xlsx';
import { Button } from "react-bootstrap";
import { Skeleton } from "primereact/skeleton";
import { ThemeContext } from "../../../../theme/ThemeContext";

const QuizMCQ = () => {
    const navigate = useNavigate();
    const { quizName } = useParams();
    const { state } = useLocation(); 
    const storedId = sessionStorage.getItem("quizId");
    const id = state?.id  || storedId;
    const [loading, setLoading] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [deletedIds, setDeletedIds] = useState([]);
    const [permissions, setPermissions] = useState({});
    const authContext = useContext(AuthContext);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const submitControllerRef = useRef(null);


    const generateUniqueId = useCallback(() => 'New_' + Date.now().toString(36) + Math.random().toString(36).substr(2), []);

    useEffect(() => {
        const quizAccess = authContext.getPermissions("quizmaster");
        if (quizAccess.filter((f) => f.access.includes(1)).length === 0) {
            navigate("/");
        }
        setPermissions({
            showAdd: quizAccess.some(f => f.access.includes(2)),
            showEdit: quizAccess.some(f => f.access.includes(3)),
            showDelete: quizAccess.some(f => f.access.includes(4)),
        });
    }, [authContext, navigate]);

    const handleAddQuestion = useCallback(async () => {
        let newQ = {
            id: generateUniqueId(),
            quizId: state?.id,
            question: "",
            orderBy: questions.length + 1,
            hasGraceMark: false,
            options: [
                { id: generateUniqueId(), option: "", isAnswer: false, orderBy: 1 },
                { id: generateUniqueId(), option: "", isAnswer: false, orderBy: 2 },
                { id: generateUniqueId(), option: "", isAnswer: false, orderBy: 3 },
                { id: generateUniqueId(), option: "", isAnswer: false, orderBy: 4 },
                { id: generateUniqueId(), option: "", isAnswer: false, orderBy: 5 },
            ],
        };
        setQuestions(prevQuestions => [...prevQuestions, newQ]);
    }, [state?.id, generateUniqueId, questions.length]);

const loadQuestions = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await QuizMCQService.GetAll(
        id,
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setQuestions(data.data);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext, id]);

    useEffect(() => {
        if (questions.length === 0) {
            handleAddQuestion();
        }
    }, [questions.length, handleAddQuestion]);

 useEffect(() => {
    const controller = new AbortController(); 
    if (id) {
      loadQuestions(controller.signal); 
    }
    return () => controller.abort(); 
  }, [loadQuestions, authContext, id]);

    const handleQuestionTextChange = useCallback((questionId, question) => {
        setQuestions(prevQuestions =>
            prevQuestions.map(q =>
                q.id === questionId ? { ...q, question: question } : q // Find by ID, update immutably
            )
        );
    }, []);

    const handleGraceMarkChange = useCallback((questionId, hasGraceMark) => {
        setQuestions(prevQuestions =>
            prevQuestions.map(q =>
                q.id === questionId ? { ...q, hasGraceMark: hasGraceMark } : q // Find by ID, update immutably
            )
        );
    }, []);

    const handleAddOption = useCallback((questionId) => {
        setQuestions(prevQuestions =>
            prevQuestions.map(q =>
                q.id === questionId
                    ? {
                        ...q,
                        options: [...q.options,
                        {
                            id: generateUniqueId(),
                            option: "",
                            isAnswer: false,
                            orderBy: q.options.length + 1,
                        }], // Add new option with unique ID
                    }
                    : q
            )
        );
    }, [generateUniqueId]);

    const handleOptionTextChange = useCallback((questionId, optionId, option, isAnswer) => {
        setQuestions(prevQuestions =>
            prevQuestions.map(q =>
                q.id === questionId
                    ? {
                        ...q,
                        options: q.options.map(opt =>
                            opt.id === optionId ? { ...opt, option: option, isAnswer: isAnswer } : (isAnswer ? { ...opt, isAnswer: false } : opt) // Find option by ID, update immutably
                        ),
                    }
                    : q
            )
        );
    }, []);

    const handleDeleteOption = useCallback((questionId, optionIdToDelete) => {
        setQuestions(prevQuestions =>
            prevQuestions.map(q => {
                if (q.id === questionId) {
                    const updatedOptions = q.options
                        .filter(opt => opt.id !== optionIdToDelete)
                        .map((opt, index) => ({ // Re-calculate orderBy for remaining options
                            ...opt,
                            orderBy: index + 1
                        }));
                    return {
                        ...q,
                        options: updatedOptions
                    };
                }
                return q;
            })
        );
    }, []);

    const removeHandler = useCallback((qIdToDelete) => {
        setQuestions((prevQuestions) => {
            const filteredQuestions = prevQuestions.filter(f => f.id !== qIdToDelete);
            // Re-calculate orderBy for remaining questions
            const updatedQuestions = filteredQuestions.map((q, index) => ({
                ...q,
                orderBy: index + 1
            }));
            return updatedQuestions;
        });

        // Add to deletedIds if it's an existing question (not a 'New_' temp ID)
        if (!qIdToDelete.toString().startsWith('New_')) {
            setDeletedIds((prev) => [...prev, qIdToDelete]);
        }
    }, []);

    const prepareQuestionsPayload = useCallback(() => {
        return questions.map(q => ({
            id: q.id.toString().startsWith('New_') ? 0 : q.id,
            quizId: q.quizId,
            question: q.question,
            hasGraceMark: q.hasGraceMark,
            orderBy: q.orderBy,
            options: q.options.map(o => ({
                id: o.id.toString().startsWith('New_') ? 0 : o.id,
                questionId: q.id.toString().startsWith('New_') ? 0 : q.id,
                option: o.option,
                isAnswer: o.isAnswer,
                orderBy: o.orderBy
            }))
        }));
    }, [questions]);

    const onCancelHandler = useCallback(() => {
        navigate(`/masters-quizzes/${quizName}`, { state: { id: id } });
    }, [navigate, quizName, id])


const onSubmitHandler = useCallback(async (event) => {
  event.preventDefault();

  if (questions.length === 0) return;

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const payload = {
      ToBeDeleted: deletedIds,
      models: prepareQuestionsPayload(),
    };

    const data = await QuizMCQService.BulkSave(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data.isSuccess) {
      AlertService.success("Questions saved successfully!");
      onCancelHandler();
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (ex) {
    if (ex.name === "AbortError") return;
    console.log(ex);
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
}, [questions, deletedIds, authContext, onCancelHandler, prepareQuestionsPayload]);

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

    const RecalculateScore = useCallback(async (e) => {
        e.preventDefault();
        let result = await QuizMCQExamService.CalculateScore(id, authContext);
        if (result.isSuccess) {
            AlertService.success("Score calculated successfully.");
        } else {
            AlertService.error(result.errorMessage);
        }
    }, [authContext, id]);

    const onImportHandler = () => {
        navigate(`/masters-quizzes/${quizName}/mcq/import`, { state: { id: id } });
    };

    return (
        <div className="container-fluid custom-container mt-3">
            <div className="mb-3">
                <div className="d-flex flex-column flex-md-row justify-content-md-end align-items-start align-items-md-center gap-2 gap-md-5 px-3">
                    <button className='text-primary d-flex align-items-center card-btn-link'
                        onClick={onImportHandler}>
                        <img
                            src={isDark ? WhiteUploadIcon : UploadIcon}
                            alt="Upload questions"
                            className="me-2"
                        /> Upload Questions</button>
                    <a className=' text-primary'
                        href={QuizMCQImportTemplate}
                        download="Quiz MCQ Import Template.xlsx">
                        <img
                            src={isDark ? WhiteDownloadIcon : DownloadIcon}
                            alt="Download template"
                            className="me-2"
                        /> Download Template</a>
                    <button
                        onClick={RecalculateScore}
                        className="text-primary d-flex align-items-center"
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'blue',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            font: 'inherit',
                            padding: 0
                        }}
                    ><img
                            src={isDark ? WhiteCalcIcon : CalcIcon}
                            alt="Re-Calculate Score"
                            className="me-2"
                        />
                        Calculate Score
                    </button>
                </div>
            </div>
            {
                loading ? (
                    Array.from({ length: 3 }).map((_, idx) => (
                        <div key={idx} >
                            <div className="row m-0 pl-3 pr-3 mb-3">
                                <div className="col-12">
                                    <Skeleton width="20%" height="1.5rem" />
                                </div>
                            </div>
                            <div className="d-flex ps-3 pe-3 mb-3">
                                <div style={{ width: '95%' }} className="pe-1">
                                    <Skeleton width="100%" height="2rem" />
                                </div>
                                <center style={{ width: '5%' }} className="pe-1">
                                    <Skeleton width="70%" height="2rem" />
                                </center>
                            </div>

                            <div className="d-flex ps-3 pe-3 mb-3">
                                <center style={{ width: '5%' }} className="pe-1">
                                    <Skeleton width="2rem" height="2rem" shape='circle' />
                                </center>
                                <div style={{ width: '90%' }} className="pe-1">
                                    <Skeleton width="100%" height="2rem" />
                                </div>
                                <center style={{ width: '5%' }} className="pe-1">
                                    <Skeleton width="70%" height="2rem" />
                                </center>
                            </div>

                            <div className="d-flex ps-3 pe-3 mb-3">
                                <center style={{ width: '5%' }} className="pe-1">
                                    <Skeleton width="2rem" height="2rem" shape='circle' />
                                </center>
                                <div style={{ width: '90%' }} className="pe-1">
                                    <Skeleton width="100%" height="2rem" />
                                </div>
                                <center style={{ width: '5%' }} className="pe-1">
                                    <Skeleton width="70%" height="2rem" />
                                </center>
                            </div>

                            <div className="d-flex ps-3 pe-3 mb-3">
                                <center style={{ width: '5%' }} className="pe-1">
                                    <Skeleton width="2rem" height="2rem" shape='circle' />
                                </center>
                                <div style={{ width: '90%' }} className="pe-1">
                                    <Skeleton width="100%" height="2rem" />
                                </div>
                                <center style={{ width: '5%' }} className="pe-1">
                                    <Skeleton width="70%" height="2rem" />
                                </center>
                            </div>

                            <div className="d-flex ps-3 pe-3 mb-3">
                                <center style={{ width: '5%' }} className="pe-1">
                                    <Skeleton width="2rem" height="2rem" shape='circle' />
                                </center>
                                <div style={{ width: '90%' }} className="pe-1">
                                    <Skeleton width="100%" height="2rem" />
                                </div>
                                <center style={{ width: '5%' }} className="pe-1">
                                    <Skeleton width="70%" height="2rem" />
                                </center>
                            </div>

                            <div className="d-flex ps-3 pe-3 mb-3">
                                <center style={{ width: '5%' }} className="pe-1">
                                    <Skeleton width="2rem" height="2rem" shape='circle' />
                                </center>
                                <div style={{ width: '90%' }} className="pe-1">
                                    <Skeleton width="100%" height="2rem" />
                                </div>
                                <center style={{ width: '5%' }} className="pe-1">
                                    <Skeleton width="70%" height="2rem" />
                                </center>
                            </div>

                            <div className="d-flex ps-3 pe-3 mb-3">
                                <center style={{ width: '5%' }} className="pe-1">
                                    <Skeleton width="2rem" height="2rem" shape="circle" />
                                </center>
                                <div style={{ width: '50%' }} className="pe-1">
                                    <Skeleton width="30%" height="2rem" />
                                </div>
                                <center className="d-flex justify-content-end pe-2" style={{ width: '45%' }}>
                                    <Skeleton width="12rem" height="2rem" />
                                </center>
                            </div>

                            <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                                <Skeleton width="100%" height="2px" />
                            </div>
                        </div>
                    ))) :
                    (questions &&
                        questions.length > 0 &&
                        questions.map((m, i) => (
                            <MCQQuestion
                                key={m.id}
                                index={i}
                                input={m}
                                onQuestionTextChange={handleQuestionTextChange}
                                onAddOption={handleAddOption}
                                onOptionTextChange={handleOptionTextChange}
                                onDeleteOption={handleDeleteOption}
                                onGraceMarkChange={handleGraceMarkChange}
                                removeHandler={removeHandler}
                            ></MCQQuestion>
                        )))
            }
            <div className="d-flex bd-highlight mb-3 pt-3 mx-2">
                <button

                    className="btn btn-primary me-auto px-4 px-md-5 py-2 me-4"
                    onClick={handleAddQuestion}
                    disabled={loading}
                >
                    Add Question
                </button>
                <button
                    className=" btn btn-outline px-4 px-md-5 py-2 me-4"
                    onClick={onCancelHandler}
                    disabled={loading}
                >
                    Cancel
                </button>
                {((permissions.showAdd && Number(state?.id) === 0) || (permissions.showEdit && Number(state?.id) !== 0)) && (
                    <Button
                        variant="primary"
                        className="px-4 px-md-5 py-2"
                        onClick={onSubmitHandler}
                        disabled={loading}
                    >
                        Submit
                    </Button>
                )}
            </div>
        </div>
    );
};

export default QuizMCQ;
